import React from 'react';

import { GridRenderCellParams } from '@mui/x-data-grid';
import { AgentPortalFeatureCode } from '@wallet-manager/pfh-pmp-node-def-types';

import APIs from '../../../api';
import { ApiResult } from '../../../api/types';
import { OpRedirect } from '../../../assets/icons';
import Table from '../../../common/filterTable/components/Table';
import FilterContainer from '../../../common/filterTable/layout/FilterContainer';
import FilterTableLayoutContainer from '../../../common/filterTable/layout/FilterTableLayoutContainer';
import FilterTopActionRow from '../../../common/filterTable/layout/FilterTopActionRow';
import TableContainer from '../../../common/filterTable/layout/TableContainer';
import useNewFilterTablePage from '../../../common/page/hooks/useNewFilterTablePage';
import { ExportBtn, OpIconButton } from '../../../components/Button';
import { useDatePicker } from '../../../components/DatePicker';
import { DivideLine } from '../../../components/Layout';
import useTranslation, { Iprefix as IPrefix } from '../../../hooks/useTranslation';
import FilterSection from './components/FilterSection';
import useGetListParams from './helpers/useGetListParams';
import useTableColumns from './helpers/useTableColumns';
import useTableRows from './helpers/useTableRows';
import { FilterFields, FilterFieldsKeys } from './types/FilterFields';
import { GetListRes } from './types/GetListRes';
import { TableRow } from './types/Table';
import ViewAssignedToDialog from './components/ViewAssignedToDialog';

const initFields = {
  [FilterFieldsKeys.createdDateFrom]: '',
  [FilterFieldsKeys.createdDateTo]: '',
  [FilterFieldsKeys.ProgramName]: '',
  [FilterFieldsKeys.QuestionType]: '',
  [FilterFieldsKeys.KycIdvMethod]: '',
};

const translatePrefix = 'programList' as IPrefix;

const getTableDataApiFunc = APIs.programAgentManagement.programList.getAll;
const exportTableApiFunc = APIs.programAgentManagement.programList.getExport;

const exportBtnPermission = AgentPortalFeatureCode.ProgramAgentManagement.ProgramList.Export;

const ProgramList = () => {
  const { getListParams } = useGetListParams();

  const DateObj = {
    CreationTime: useDatePicker(),
  };

  const pageConfig = {
    initFields,
    DateObj,
    translatePrefix,
    getListParams,
    getTableDataApiFunc,
    exportTableApiFunc,
  };

  const {
    page,
    onPageChange,
    fields,
    setFields,
    list,
    onFilterSearch,
    onFilterReset,
    onExport,
    getIsHavePermission,
    Tabs,
    refreshTable,
  } = useNewFilterTablePage<FilterFields, ApiResult<GetListRes>>(pageConfig);

  const [selectedRow, setSelectedRow] = React.useState<TableRow | undefined>(undefined);
  const [isViewAssignedToDialogOpen, setIsViewAssignedToDialogOpen] = React.useState(false);

  const { t } = useTranslation(translatePrefix);
  const { rows, totalCounts, convertToTableRows } = useTableRows({ apiList: list });
  const columns = useTableColumns({ renderViewAssignedToCell });

  const isShowExportBtn = getIsHavePermission(exportBtnPermission);

  const handleExport = async () => {
    await onExport('Program List', totalCounts, convertToTableRows);
  };

  function renderViewAssignedToCell(params: GridRenderCellParams) {
    const handleOnClick = () => {
      setSelectedRow(params.row);
      setIsViewAssignedToDialogOpen(true);
    };

    return (
      <OpIconButton
        title={t('assignedTo')}
        svgUrl={OpRedirect}
        onClick={handleOnClick}
        size="1.5rem"
      />
    );
  }

  return (
    <>
      <FilterTableLayoutContainer>
        <ViewAssignedToDialog
          isOpen={isViewAssignedToDialogOpen}
          selectedRow={selectedRow}
          closeDialog={() => {
            setIsViewAssignedToDialogOpen(false);
            setSelectedRow(undefined);
          }}
          refreshTable={refreshTable}
        />
        <FilterContainer>
          {/* <FilterTopActionRow></FilterTopActionRow> */}
          <FilterSection
            fields={fields}
            setFields={setFields}
            onSearch={onFilterSearch}
            onReset={onFilterReset}
            DateObj={DateObj}
          />
        </FilterContainer>

        <DivideLine />

        <TableContainer>
          <ExportBtn onExport={handleExport} isShow={isShowExportBtn} />
          <Tabs>
            <Table
              columns={columns}
              rows={rows}
              rowCount={totalCounts}
              page={page}
              onPageChange={onPageChange}
            />
          </Tabs>
        </TableContainer>
      </FilterTableLayoutContainer>
    </>
  );
};

export default ProgramList;
